import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import type { DocsParam } from '../generated/reference';

import { formatValue, schemaTypeLabel } from './schema-viewer-type';

/** Path, query or header parameters of an operation: name, type, required, description, example. */
export function ParamTable({ params, label }: { params: DocsParam[]; label: string }) {
  const { t } = useTranslation('docs');
  return (
    <div className="rounded-lg border">
      <Table aria-label={label} className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead className="w-48 pl-4">{t('endpoint.params.name')}</TableHead>
            <TableHead>{t('endpoint.params.type')}</TableHead>
            <TableHead className="min-w-64">{t('endpoint.params.description')}</TableHead>
            <TableHead className="pr-4">{t('endpoint.params.example')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {params.map((param) => {
            const example = param.example ?? param.schema.example;
            const description = param.description || param.schema.description;
            return (
              <TableRow key={`${param.in}:${param.name}`}>
                <TableCell className="pl-4 align-top">
                  <div className="flex flex-col gap-0.5">
                    <code className="font-mono font-semibold">{param.name}</code>
                    <span
                      className={
                        param.required
                          ? 'text-destructive text-xs font-medium'
                          : 'text-muted-foreground text-xs'
                      }
                    >
                      {param.required
                        ? t('endpoint.params.required')
                        : t('endpoint.params.optional')}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <code className="text-muted-foreground font-mono text-xs">
                    {schemaTypeLabel(param.schema)}
                  </code>
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <div className="flex flex-col gap-1.5">
                    {description ? <span>{description}</span> : null}
                    {param.schema.enum?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {param.schema.enum.map((value) => (
                          <Badge key={String(value)} variant="outline" className="font-mono">
                            {formatValue(value)}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="pr-4 align-top">
                  {example !== undefined ? (
                    <code className="font-mono text-xs break-all">{formatValue(example)}</code>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
